/**
 * Gestion centralisée des erreurs d'authentification
 * Version épurée et optimisée de l'ancien errorMessages.ts
 */

export interface SupabaseError {
  code?: string;
  message?: string;
  name?: string;
}

/**
 * Convertit une erreur Supabase en message utilisateur français
 */
export function getAuthErrorMessage(error: SupabaseError | null | undefined): string {
  if (!error) return "Une erreur inattendue s'est produite";

  // Mapping des codes d'erreur vers des messages utilisateur
  const errorMessages: Record<string, string> = {
    // Erreurs d'authentification
    invalid_credentials: 'Email ou mot de passe incorrect',
    email_not_confirmed: 'Email ou mot de passe incorrect',
    signup_disabled: 'Les inscriptions sont actuellement désactivées',
    user_not_found: "Aucun compte n'est associé à cette adresse e-mail",

    // Erreurs de mot de passe
    weak_password: 'Le mot de passe doit contenir au moins 6 caractères',
    same_password: "Le nouveau mot de passe doit être différent de l'ancien",

    // Erreurs d'email
    email_address_invalid: "Cette adresse e-mail n'est pas valide",
    email_rate_limit_exceeded: 'Trop de tentatives. Veuillez patienter avant de réessayer',
    over_email_send_rate_limit: 'Trop de tentatives. Veuillez patienter avant de réessayer',

    // Erreurs de session
    session_not_found: 'Session expirée, veuillez vous reconnecter',
    refresh_token_not_found: 'Session expirée, veuillez vous reconnecter',

    // Erreurs réseau
    network_error: 'Problème de connexion, vérifiez votre réseau',
    fetch_error: 'Problème de connexion, vérifiez votre réseau',
  };

  // Recherche par code d'erreur
  if (error.code && errorMessages[error.code]) {
    return errorMessages[error.code];
  }

  // Fallback sur le message si code non reconnu
  if (error.message) {
    const message = error.message.toLowerCase();

    if (message.includes('invalid login credentials')) {
      return 'Email ou mot de passe incorrect';
    }
    if (message.includes('email not confirmed')) {
      return 'Veuillez confirmer votre adresse email';
    }
    if (message.includes('same password') || message.includes('different from the old password')) {
      return "Le nouveau mot de passe doit être différent de l'ancien";
    }
    if (message.includes('network')) {
      return 'Problème de connexion, vérifiez votre réseau';
    }
  }

  return 'Erreur de connexion, veuillez réessayer';
}

/**
 * Vérifie si une erreur est temporaire (peut être retentée)
 */
export function isTemporaryError(error: SupabaseError | null | undefined): boolean {
  if (!error) return false;

  const temporaryCodes = [
    'email_rate_limit_exceeded',
    'over_email_send_rate_limit',
    'network_error',
    'fetch_error',
  ];

  return temporaryCodes.includes(error.code || '');
}

/**
 * Vérifie si une erreur nécessite une reconnexion
 */
export function requiresReauth(error: SupabaseError | null | undefined): boolean {
  if (!error) return false;

  const reauthCodes = ['session_not_found', 'refresh_token_not_found'];

  return reauthCodes.includes(error.code || '');
}
